import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { 
  getArticleById,
  markArticleViewed,
  toggleArticleBookmark,
  KNOWLEDGE_BASE_CATEGORIES
} from '../../utils/knowledgeBaseData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';

const KnowledgeBaseArticle = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key for forcing updates
  const [bookmarked, setBookmarked] = useState(false); // Track bookmark state separately
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = () => {
    try {
      const foundArticle = getArticleById(articleId);
      
      if (foundArticle) {
        setArticle(foundArticle);
        setBookmarked(foundArticle.bookmarked); // Set bookmark state
        // Mark as viewed
        markArticleViewed(articleId);
      } else {
        dispatch(setErrorAlert('Article not found'));
        navigate('/knowledge-base');
      }
    } catch (error) {
      console.error('Error loading article:', error);
      dispatch(setErrorAlert('Failed to load article'));
      navigate('/knowledge-base');
    }
  };

  const handleBookmarkToggle = () => {
    try {
      const isBookmarked = toggleArticleBookmark(articleId);
      // Update bookmark state immediately for UI feedback
      setBookmarked(isBookmarked);
      
      // Reload the article to get the properly instantiated object with methods
      const updatedArticle = getArticleById(articleId);
      setArticle(updatedArticle);
      setRefreshKey(prev => prev + 1); // Force re-render
      
      dispatch(setSuccessAlert(isBookmarked ? 'Article bookmarked' : 'Bookmark removed'));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      dispatch(setErrorAlert('Failed to update bookmark'));
    }
  };

  const handleBack = () => {
    navigate('/knowledge-base');
  };

  const getCategoryColor = (categoryName) => {
    const category = Object.values(KNOWLEDGE_BASE_CATEGORIES).find(cat => cat.name === categoryName);
    return category ? category.color : '#007AFF';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#34C759';
      case 'Intermediate': return '#FF9500';
      case 'Advanced': return '#FF3B30';
      default: return '#007AFF';
    }
  };

  const formatContent = (content) => {
    // Convert markdown-like formatting to HTML
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('**') && line.endsWith('**')) {
          // Bold headers
          return (
            <h4 key={index} className="text-white text-[13px] font-semibold mt-4 mb-2">
              {line.slice(2, -2)}
            </h4>
          );
        } else if (line.startsWith('•')) {
          // Bullet points
          return (
            <li key={index} className="text-[rgba(255,255,255,0.8)] text-[11px] leading-[1.6] ml-4 mb-1">
              {line.slice(1).trim()}
            </li>
          );
        } else if (line.match(/^\d+\./)) {
          // Numbered lists
          return (
            <li key={index} className="text-[rgba(255,255,255,0.8)] text-[11px] leading-[1.6] ml-4 mb-1 list-decimal">
              {line.replace(/^\d+\./, '').trim()}
            </li>
          );
        } else if (line.trim() === '') {
          // Empty lines
          return <br key={index} />;
        } else {
          // Regular paragraphs
          return (
            <p key={index} className="text-[rgba(255,255,255,0.8)] text-[11px] leading-[1.6] mb-2">
              {line}
            </p>
          );
        }
      });
  };

  if (!article) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Loading article...
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF1493]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  if (!article) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Article not found
              </div>
              <button 
                onClick={() => navigate('/knowledge-base')}
                className="bg-[#FF1493] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(255,20,147,0.8)] transition-colors"
              >
                Back to Knowledge Base
              </button>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Knowledge</span>
            <div className="flex items-center gap-4">
              <button
                key={refreshKey} // Force re-render when bookmark changes
                onClick={handleBookmarkToggle}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                {bookmarked ? '★ Bookmarked' : '☆ Bookmark'}
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={handleBack}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {/* Article Header */}
            <div className="mb-6">
              <h1 className="text-white text-[18px] font-bold mb-3 leading-tight">
                {article.title}
              </h1>
              
              <div className="flex items-center gap-2 mb-3">
                <span 
                  className="text-[10px] px-3 py-1 rounded font-medium"
                  style={{ 
                    backgroundColor: `${getCategoryColor(article.category)}20`,
                    color: getCategoryColor(article.category)
                  }}
                >
                  {article.category}
                </span>
                <span 
                  className="text-[10px] px-3 py-1 rounded font-medium"
                  style={{ 
                    backgroundColor: `${getDifficultyColor(article.difficulty)}20`,
                    color: getDifficultyColor(article.difficulty)
                  }}
                >
                  {article.difficulty}
                </span>
              </div>

              <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-4 leading-[1.5]">
                {article.summary}
              </p>

              <div className="flex items-center gap-4 text-[10px] text-[rgba(255,255,255,0.5)] mb-6 pb-4 border-b border-[rgba(255,255,255,0.1)]">
                <span>{article.getReadingTime()} min read</span>
                <span>{article.timeToComplete}</span>
              </div>
            </div>

            {/* Article Content */}
            <div className="mb-8">
              <div className="prose prose-invert max-w-none">
                {formatContent(article.content)}
              </div>
            </div>

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mb-6">
                <h4 className="text-white text-[12px] font-semibold mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <span 
                      key={tag}
                      className="text-[10px] px-3 py-1 rounded bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.2)] transition-colors cursor-pointer"
                      onClick={() => {
                        navigate('/knowledge-base');
                        // Could implement tag search here
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default KnowledgeBaseArticle;